import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import { completeProfile } from "../../Features/Postes/Thunk/authThunk";

import "../Prescriptions/Prescriptions.scss";
import "./CompleteProfile.scss";
import PatientFlowStepper, {
  buildPatientFlowSteps,
} from "../../components/PatientFlowStepper/PatientFlowStepper";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import API from "../../Api/Api";
import ENDPOINTS from "../../Api/Endpoints";
import { tokenManager } from "../../Api/tokenManager";
import editpen from "../../../src/Assets/penedit.png";
import DEFAULT_AVATAR from "../../Assets/profile.png";
import { showAlert } from "../../feedback/feedbackApi";

const NARROW_APP_BAR_MQ = "(max-width: 1023px)";

const formatDobForCard = (value) => {
  const v = String(value || "").trim();
  if (!v) return "—";
  const parts = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) {
    const y = parseInt(parts[1], 10);
    const mo = parseInt(parts[2], 10) - 1;
    const day = parseInt(parts[3], 10);
    const dt = new Date(y, mo, day);
    if (dt.getFullYear() === y && dt.getMonth() === mo && dt.getDate() === day) {
      return dt.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  }
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return v;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/** Normalize API / ISO values to `YYYY-MM-DD` for `<input type="date" />`. */
const toDateInputValue = (value) => {
  const v = String(value || "").trim();
  if (!v) return "";
  const parts = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (parts) return `${parts[1]}-${parts[2]}-${parts[3]}`;
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
};

const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

/**
 * @param {{ appVariant?: boolean }} props — `true` when routed as `/patient-profile/details` inside PatientAppShell:
 *   no onboarding stepper, app bar + back to menu; save shows a toast and stays on this page.
 */
export default function CompleteProfile({ appVariant = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { profileLoading } = useSelector((state) => state.auth);

  const steps = useMemo(() => buildPatientFlowSteps(t), [t]);

  const handleStepClick = (stepIndex) => {
    const s = location.state;
    if (stepIndex >= 0 && stepIndex <= 2) navigate("/request/new", { state: s });
    if (stepIndex === 3) navigate("/request/new", { state: s });
  };

  const fileRef = useRef(null);

  const [avatar, setAvatar] = useState(DEFAULT_AVATAR);
  const [imageFile, setImageFile] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [bloodGroup, setBloodGroup] = useState("");
  const [gender, setGender] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(
    () => !tokenManager.getTokenForRole("patient")
  );
  const [editingInApp, setEditingInApp] = useState(false);
  const [accountAvatarFailed, setAccountAvatarFailed] = useState(false);
  const [narrowAppBar, setNarrowAppBar] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(NARROW_APP_BAR_MQ).matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia(NARROW_APP_BAR_MQ);
    const onChange = () => setNarrowAppBar(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setAccountAvatarFailed(false);
  }, [avatar]);

  useEffect(() => {
    const token = tokenManager.getTokenForRole("patient");
    if (!token) {
      setProfileLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API.get(ENDPOINTS.GET_ME, {
          params: { _t: Date.now() },
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });
        const body = res?.data ?? res;
        const user = body?.user ?? body?.data?.user ?? null;
        if (cancelled || !user) return;
        if (user.name) setName(String(user.name).trim());
        if (user.email) setEmail(String(user.email).trim());
        if (user.phone || user.mobile || user.mobileNo) {
          setPhone(String(user.phone || user.mobile || user.mobileNo).trim());
        }
        if (user.dateOfBirth || user.dob) {
          setDob(toDateInputValue(String(user.dateOfBirth || user.dob).trim()));
        }
        if (user.bloodGroup || user.blood_type) {
          setBloodGroup(String(user.bloodGroup || user.blood_type).trim());
        }
        const g = user.gender;
        if (g === "Male" || g === "Female" || g === "Others") setGender(g);
        const av =
          user.avatar ||
          user.profileImageUrl ||
          user.profile_picture ||
          user.photoUrl ||
          body?.avatar ||
          body?.profileImageUrl ||
          "";
        if (av) setAvatar(String(av).trim());
      } catch {
        /* keep empty form */
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onNameChange = (e) => {
    const raw = e.target.value;
    const filtered = raw.replace(/[^a-zA-Z\s]/g, "");
    const cleaned = filtered.replace(/\s+/g, " ").replace(/^\s+/, "");
    setName(cleaned);
  };

  const nameError = useMemo(() => {
    const trimmed = name.trim();
    if (!trimmed) return "Name is required.";
    if (trimmed.length < 2) return "Name must be at least 2 characters.";
    return "";
  }, [name]);

  const canSubmit = useMemo(() => {
    const base = !nameError && Boolean(gender);
    if (!appVariant) return base;
    return (
      base &&
      Boolean(String(dob || "").trim()) &&
      Boolean(String(bloodGroup || "").trim())
    );
  }, [nameError, gender, dob, bloodGroup, appVariant]);

  const onPickImage = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);

    const url = URL.createObjectURL(file);
    setAvatar(url);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const res = await dispatch(
        completeProfile({ name, gender, dob, bloodGroup, image: imageFile })
      ).unwrap();

      const s3Url =
        res?.profileImageUrl ||
        res?.profile_image_url ||
        res?.avatar ||
        res?.user?.avatar ||
        res?.data?.profileImageUrl ||
        res?.data?.profile_image_url ||
        res?.data?.avatar ||
        res?.data?.user?.avatar;

      if (s3Url) {
        setAvatar(s3Url);
      }

      if (appVariant) {
        setImageFile(null);
        setEditingInApp(false);
        showAlert("Your profile was updated.", { variant: "success", title: "Saved" });
        return;
      }
      navigate("/request/new");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        (typeof err === "string" ? err : null) ||
        "Something went wrong.";
      showAlert(String(msg), { variant: "error", title: "Could not save profile" });
    }
  };

  const cardTitle = appVariant ? "Your details" : "Complete Your Profile";
  const submitLabel = profileLoading
    ? "Saving..."
    : appVariant
      ? "Save changes"
      : "Complete Profile";

  const onEditPhotoKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onPickImage();
    }
  };

  const avatarBlock = (
    <div className={`cp2AvatarWrap${appVariant ? " cp2AvatarWrap--app" : ""}`}>
      <div className="cp2AvatarRing">
        <img className="cp2Avatar" src={avatar} alt="" />
      </div>

      <span
        className="cp2EditBtn"
        onClick={onPickImage}
        onKeyDown={onEditPhotoKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Change profile photo"
      >
        <img src={editpen} alt="" />
      </span>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onFileChange}
      />
    </div>
  );

  const profileInitials =
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((chunk) => chunk[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "PN";

  const accountCard = (
    <section className="cp2AccountCard" aria-label="Patient account details">
      <div className="cp2AccountCard__head">
        <div className="cp2AccountCard__avatarSlot">
          {!accountAvatarFailed ? (
            <img
              className="cp2AccountCard__avatarImg"
              src={avatar}
              alt=""
              onError={() => setAccountAvatarFailed(true)}
            />
          ) : (
            <div className="cp2AccountCard__chip" aria-hidden>
              {profileInitials}
            </div>
          )}
        </div>
        <div className="cp2AccountCard__identity">
          <p className="cp2AccountCard__name">{name.trim() || "Pritam Nandi"}</p>
          <p className="cp2AccountCard__subtle">
            {[String(email || "").trim(), String(phone || "").trim()].filter(Boolean).join(" • ") || "—"}
          </p>
        </div>
      </div>

      <div className="cp2AccountCard__grid">
        <div className="cp2AccountCard__item">
          <p className="cp2AccountCard__label">Date of birth</p>
          <p className="cp2AccountCard__value">{formatDobForCard(dob)}</p>
        </div>
        <div className="cp2AccountCard__item">
          <p className="cp2AccountCard__label">Blood group</p>
          <p className="cp2AccountCard__value">{bloodGroup || "—"}</p>
        </div>
      </div>

      <div className="cp2AccountCard__actions">
        <button
          type="button"
          className="cp2AccountCard__btn cp2AccountCard__btn--primary"
          onClick={() => setEditingInApp((prev) => !prev)}
        >
          {editingInApp ? "Close editor" : "Edit profile"}
        </button>
      </div>
    </section>
  );

  const formCard = (
    <div className={`cp2Card${appVariant ? " cp2Card--app cp2Card--premium" : ""}`}>
      {!appVariant ? <div className="cp2Title">{cardTitle}</div> : null}

      {appVariant ? (
        <div className="cp2AppHero">
          {avatarBlock}
          <div className="cp2AppIdentity">
            <p className="cp2AppIdentity__name">
              {name.trim() || "Your name"}
            </p>
            <p className="cp2AppIdentity__hint">
              Tap the pencil to change your photo
            </p>
          </div>
        </div>
      ) : (
        avatarBlock
      )}

      <form
        onSubmit={onSubmit}
        className={`cp2Form${appVariant ? " cp2Form--app" : ""}`}
      >
        {appVariant ? (
          <h2 className="cp2AppSectionTitle">Personal details</h2>
        ) : null}
        <div className="cp2Field">
          <div className="cp2Label">Full Name</div>
          <input
            value={name}
            onChange={onNameChange}
            onBlur={() => setNameTouched(true)}
            placeholder="Enter your name"
            autoComplete="name"
          />
          {nameTouched && nameError ? (
            <small style={{ color: "#d14343", marginTop: 6, display: "block" }}>
              {nameError}
            </small>
          ) : null}
        </div>

        <div className="cp2Field">
          <div className="cp2Label">Gender</div>

          <div className="cp2Select">
            <select value={gender} onChange={(e) => setGender(e.target.value)}>
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Others">Other</option>
            </select>
            <span className="cp2Caret">▾</span>
          </div>
        </div>

        <div className="cp2Field">
          <div className="cp2Label">Date of birth</div>
          <input
            type="date"
            value={dob}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => setDob(e.target.value)}
            autoComplete="bday"
          />
        </div>

        <div className="cp2Field">
          <div className="cp2Label">Blood group</div>
          <div className="cp2Select">
            <select value={bloodGroup} onChange={(e) => setBloodGroup(e.target.value)}>
              <option value="">Select blood group</option>
              {BLOOD_GROUP_OPTIONS.map((bg) => (
                <option key={bg} value={bg}>
                  {bg}
                </option>
              ))}
            </select>
            <span className="cp2Caret">▾</span>
          </div>
        </div>

        <button
          className={`cp2Btn${appVariant ? " cp2Btn--premium" : ""}`}
          disabled={!canSubmit || profileLoading}
        >
          {submitLabel}
        </button>
      </form>
    </div>
  );

  const appBarEditSlot =
    appVariant && narrowAppBar && profileLoaded ? (
      <button
        type="button"
        className="patient-app-bar__screenTitleAction"
        onClick={() => setEditingInApp((prev) => !prev)}
        aria-label={editingInApp ? "Close profile editor" : "Edit profile"}
      >
        {editingInApp ? <X size={22} strokeWidth={2.1} aria-hidden /> : <Pencil size={21} strokeWidth={2} aria-hidden />}
      </button>
    ) : null;

  if (appVariant) {
    return (
      <PatientAppLayout activeNav="profile" appBarEndSlot={appBarEditSlot}>
        <div className="cp2Page cp2Page--app">
          <div className="cp2Flow cp2Flow--app">
            {!profileLoaded ? (
              <div className="cp2AppLoading" role="status" aria-live="polite">
                Loading your profile…
              </div>
            ) : (
              <>
                {accountCard}
                {editingInApp ? formCard : null}
              </>
            )}
          </div>
        </div>
      </PatientAppLayout>
    );
  }

  return (
    <div className="cp2Page">
      <div className="cp2Flow">
        <PatientFlowStepper
          steps={steps}
          activeStep={0}
          onStepClick={handleStepClick}
          extraClass="cp2Stepper"
        />

        {!profileLoaded ? (
          <div className="cp2AppLoading" role="status" aria-live="polite">
            Loading…
          </div>
        ) : (
          formCard
        )}
      </div>
    </div>
  );
}
